import { useEffect, useRef, useState } from "react";

interface ScrollAnimationOptions {
  threshold?: number;
  rootMargin?: string;
  triggerOnce?: boolean;
}

export function useScrollAnimation(options: ScrollAnimationOptions = {}) {
  const { threshold = 0.1, rootMargin = "0px", triggerOnce = true } = options;
  const ref = useRef<HTMLDivElement>(null);
  // Start as true so content is visible before JS sets up the observer
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) {
            observer.unobserve(element);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);

    // Only hide the element AFTER the observer is active. If the element is
    // already in the viewport the callback fires before this state update is
    // applied, so the "hidden" flash never occurs for above-the-fold content.
    // For below-fold content the element isn't in the viewport yet so hiding
    // it is safe; the user will see the animated reveal when they scroll down.
    setIsVisible(false);

    return () => {
      observer.unobserve(element);
    };
  }, [threshold, rootMargin, triggerOnce]);

  return { ref, isVisible };
}

interface AnimatedSectionProps {
  children: React.ReactNode;
  className?: string;
  animation?: "fade-up" | "fade-down" | "fade-left" | "fade-right" | "scale" | "blur";
  delay?: number;
  duration?: number;
  threshold?: number;
}

export function AnimatedSection({
  children,
  className = "",
  animation = "fade-up",
  delay = 0,
  duration = 600,
  threshold = 0.1,
}: AnimatedSectionProps) {
  const ref = useRef<HTMLDivElement>(null);
  // isSetup gates whether we apply any animation styles at all.
  // Before the effect runs (or if JS is disabled), no inline styles are
  // applied and the content is fully visible via normal CSS — satisfying
  // the "visible by default" requirement.
  const [isSetup, setIsSetup] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) return;

    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(element);
        }
      },
      { threshold }
    );

    observer.observe(element);
    // Arm the animation AFTER the observer is active.
    setIsSetup(true);

    return () => {
      observer.disconnect();
    };
  }, [threshold]);

  // No animation setup yet → render with no inline styles (fully visible).
  if (!isSetup) {
    return (
      <div ref={ref} className={className}>
        {children}
      </div>
    );
  }

  const baseStyles: React.CSSProperties = {
    transition: `opacity ${duration}ms ease-out, transform ${duration}ms ease-out, filter ${duration}ms ease-out`,
    transitionDelay: `${delay}ms`,
  };

  const hiddenStyles: Record<string, React.CSSProperties> = {
    "fade-up": { opacity: 0, transform: "translateY(20px)" },
    "fade-down": { opacity: 0, transform: "translateY(-20px)" },
    "fade-left": { opacity: 0, transform: "translateX(20px)" },
    "fade-right": { opacity: 0, transform: "translateX(-20px)" },
    scale: { opacity: 0, transform: "scale(0.97)" },
    blur: { opacity: 0, filter: "blur(4px)" },
  };

  const visibleStyles: React.CSSProperties = {
    opacity: 1,
    transform: "translate(0) scale(1)",
    filter: "blur(0)",
  };

  const styles = isVisible
    ? { ...baseStyles, ...visibleStyles }
    : { ...baseStyles, ...hiddenStyles[animation] };

  return (
    <div ref={ref} className={className} style={styles}>
      {children}
    </div>
  );
}

export function AnimatedCard({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  return (
    <AnimatedSection animation="fade-up" delay={delay} className={className}>
      <div className="interactive-card h-full">{children}</div>
    </AnimatedSection>
  );
}

export function AnimatedList({
  children,
  className = "",
  staggerDelay = 100,
}: {
  children: React.ReactNode[];
  className?: string;
  staggerDelay?: number;
}) {
  return (
    <div className={className}>
      {children.map((child, index) => (
        <AnimatedSection key={index} animation="fade-up" delay={index * staggerDelay}>
          {child}
        </AnimatedSection>
      ))}
    </div>
  );
}
